import React, { useState } from "react";
import {
  courseResponse,
  relatedItemRequest,
} from "../../../interfaces/new-api-interfaces/course";
import { SubmitHandler, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  fetchBlogs,
  fetchCourseRelatedItems,
  fetchCourses,
  fetchShopItems,
} from "../../../api/endpoints-functions";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import AddRelatedItem from "../DashboardCourseComponents/add-related-item";
import { defaultTranslation } from "../../../untilites/functions";
import { fetchApi } from "../../../api/api";
import { toast } from "react-toastify";
import i18next from "i18next";
import { shopItemResponse } from "../../../interfaces/new-api-interfaces/shop-item";
import { BlogResponse } from "../../../interfaces/new-api-interfaces/blog";
import { ErrorFallBackNotFound } from "../../Common/error-handling/error-404";
import { useTranslation } from "react-i18next";

type Props = {
  id: number;
  handleNext: Function;
};
const inputStyle = {
  width: { xs: "100%", sm: "92%" },
  marginBottom: "30px",
};

const EditRelatedCourseItems = (props: Props) => {
  const { t } = useTranslation();
  const [modifiedCourses, setModifiedCourses] = useState<
    { id: number; type: string; name: string }[]
  >([]);

  const [modifiedShopItems, setModifiedShopItems] = useState<
    { id: number; type: string; name: string }[]
  >([]);

  const [modifiedBlogs, setModifiedBlogs] = useState<
    { id: number; type: string; name: string }[]
  >([]);

  const { isLoading: loadingRequest, mutateAsync } = useMutation(
    (data: any) =>
      fetchApi(
        `/course/relatedItem/createRelatableItem/${props.id}`,
        "POST",
        data
      ),
    {
      onError: (error: any) => {
        toast.error(
          i18next.language === "en"
            ? "Something went wrong, please try again later."
            : "حدث خطأ ما، يرجى المحاولة مرة أخرى لاحقًا."
        );
      },
    }
  );

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<relatedItemRequest[]>({});

  const onSubmit: SubmitHandler<relatedItemRequest[]> = async (data) => {
    const relatable = Object.values(data).flat();
    try {
      mutateAsync({
        relatable,
      }).then((response) => {
        if (
          response.status === 200 ||
          response.status === 201 ||
          response.status === 202
        ) {
          toast.success(
            i18next.language === "en"
              ? "Related Items Added successfully"
              : "تم إضافة العناصر المتعلقة بالدورة بنجاح"
          );
          props.handleNext();
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const {
    data: courses,
    isLoading,
    isError,
  } = useQuery(["get-courses"], () => fetchCourses(null), {
    onSuccess: (data) => {
      // Modify the data to the desired format
      const modifiedData = data.map((course) => ({
        id: course.id,
        type: "course",
        name: defaultTranslation(course, "name"),
      }));

      setModifiedCourses(modifiedData);
    },
  });

  const {
    data: shopItems,
    isLoading: l2,
    isError: e2,
  } = useQuery(["get-shop-items"], () => fetchShopItems(null), {
    onSuccess: (data) => {
      // Modify the data to the desired format
      const modifiedData = data.map((item) => ({
        id: item.id,
        type: "shopitem",
        name: defaultTranslation(item, "name"),
      }));

      setModifiedShopItems(modifiedData);
    },
  });

  const {
    data: blogs,
    isLoading: l3,
    isError: e3,
  } = useQuery(["get-blogs"], () => fetchBlogs(), {
    onSuccess: (data) => {
      // Modify the data to the desired format
      const modifiedData = data.map((item) => ({
        id: item.id,
        type: "blog",
        name: defaultTranslation(item, "name"),
      }));

      setModifiedBlogs(modifiedData);
    },
  });

  const {
    data: oldRelated,
    isLoading: loadingResponse,
    isError: errorResponse,
  } = useQuery(
    ["get-related", props.id],
    () => fetchCourseRelatedItems(props.id),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onSuccess: (data) => {
        const tempCourses: relatedItemRequest[] = [];
        const tempBlogs: relatedItemRequest[] = [];
        const tempShopItems: relatedItemRequest[] = [];

        if (data.shop_items) {
          data.shop_items.forEach((item: shopItemResponse) => {
            tempShopItems.push({
              id: item.id,
              type: "shopitem",
              name: defaultTranslation(item, "name"),
            });
          });
        }

        if (data.courses) {
          data.courses.forEach((course: courseResponse) => {
            tempCourses.push({
              id: course.id,
              type: "course",
              name: defaultTranslation(course, "name"),
            });
          });
        }

        if (data.blogs) {
          data.blogs.forEach((blog: BlogResponse) => {
            tempBlogs.push({
              id: blog.id,
              type: "blog",
              name: defaultTranslation(blog, "name"),
            });
          });
        }

        //@ts-ignore
        setValue("course", tempCourses);
        //@ts-ignore
        setValue("blog", tempBlogs);
        //@ts-ignore
        setValue("shopitem", tempShopItems);
      },
    }
  );

  if (errorResponse) return <ErrorFallBackNotFound />;
  if (isLoading || l2 || l3 || loadingRequest || loadingResponse)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  return (
    <form
      className="d-flex flex-wrap container"
      style={{
        width: "100%",
        gap: "2%",
        padding: "20px 0 50px 0",
        justifyContent: "center",
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box sx={inputStyle}>
        <AddRelatedItem
          control={control}
          tags={modifiedCourses!}
          name="course"
          label={t("addRelatedCourses")}
          watch={watch}
          setValue={setValue}
          oldValue={oldRelated?.courses}
          courseId={props.id}
        />
      </Box>

      <Box sx={inputStyle}>
        <AddRelatedItem
          control={control}
          tags={modifiedShopItems!}
          name="shopitem"
          label={t("addRelatedShopItems")}
          watch={watch}
          setValue={setValue}
          oldValue={oldRelated?.shop_items}
          courseId={props.id}
        />
      </Box>

      <Box sx={inputStyle}>
        <AddRelatedItem
          control={control}
          tags={modifiedBlogs!}
          name="blog"
          label={t("addRelatedBlogs")}
          watch={watch}
          setValue={setValue}
          oldValue={oldRelated?.blogs}
          courseId={props.id}
        />
      </Box>

      <div className="col-12 d-flex justify-content-center pt-4">
        <Button
          variant="contained"
          color="primary"
          className="mb-2 btn-hover-secondary btn-width-100"
          type="submit"
          sx={{ width: { xs: "100%", sm: "92%", lg: "30%" } }}
        >
          <Typography fontSize="large" color="#fff">
            {t("submit")}
          </Typography>
        </Button>
      </div>
    </form>
  );
};

export default EditRelatedCourseItems;
